import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import { TemplateState } from '../../../types/common/types';
import {
  WEB_COMPONENT_GENERIC_GRID_SMART_VIEWS,
  WEB_COMPONENT_GRID_VIEW,
  WEB_COMPONENT_GZ_PUSH_BODY_LAYOUT,
} from '../../../webcomponents.constants';
import { GridViewWebComponent } from '../../../grid/gridView';
import { GenericGridSmartViewsWebComponent } from '../../../grid/genericGridSmartViews';
import {
  ApplicationType,
  ColumnIndexes,
  ColumnKeys,
  VulnerabilitiesAppWebComponentSchema,
  VulnerabilitiesSources,
} from '../types';
import { ColumnType } from '../../../grid/types/common';
import { VulnerabilitiesAppGridDataProvider } from '../dataProvider/VulnerabilitiesAppGridDataProvider';
import { GzSideMenuWebComponent } from '../../../ui/gzSideMenu';
import { actionsColumnRenderer, cvesRenderer } from './CustomGridRenderers';
import {
  convertGenericViewConfigToGridViewConfig,
  ValidateViewNameErrorCodeResponse,
} from '../../../grid/genericGridSmartViews/types';
import { GzPushBodyLayoutWebComponent } from '../../../ui/gzPushBodyLayout';
import {
  changeEmptyStateIcon,
  getCompanyFilterUiConfig,
  getOSTypeColumnValues,
  getStateColumnValues,
  getWatchlistColumnValues,
  processExternalFilters,
  setBulkActions,
  setGridCommonProperties,
  setTranslatedStrings,
} from '../../common/utils';
import { translateFn } from '../../../utils/translator.helper';
import {
  affectedDevicesRenderer,
  companyRenderer,
  osRenderer,
  renderAsCellLabel,
  riskScoreRenderer,
} from '../../common/customRenderers';
import { ElementClass } from '../../../utils/htmlElement.utils';
import { RemoteDetailsTypes } from '../../../filters/backendEnumFilter/types';
import {
  getSavedSmartViewsCategory,
  setSmartViewsPanelSizeBasedOnState,
} from '../../../grid/genericGridSmartViews/utils';
import { BaseWebComponentTranslator } from '../../../framework/BaseWebComponentTranslator';
import { AffectedDeviceType, RiskTypes } from '../../common/constants';
import { AnswerType } from '../../../applicationCveList/utils';

export class VulnerabilitiesAppView extends BaseWebComponentView {
  public riskType = RiskTypes.VULNERABILITIES;
  public filterDependencyMapping = {
    companyId: ['fullApplicationName', 'cves', 'affectedDevices'],
  };

  getDomElements() {
    this.dom.$wrapper = this.shadow.querySelector('gz-push-body-layout .wrapper');
    this.dom.$title = this.shadow.querySelector('gz-push-body-layout .title');
    this.dom.$pageTitle = this.shadow.querySelector('.page_title');
    this.dom.$loader = this.shadow.querySelector('gz-push-body-layout .loader');

    this.dom.$pushBodyLayout = this.shadow.querySelector(
      WEB_COMPONENT_GZ_PUSH_BODY_LAYOUT
    ) as GzPushBodyLayoutWebComponent;

    this.dom.$grid = this.shadow.querySelector(
      `gz-push-body-layout ${WEB_COMPONENT_GRID_VIEW}`
    ) as GridViewWebComponent<ColumnIndexes, ColumnKeys>;

    this.dom.$loadingErrorMessage = this.shadow.querySelector('gz-push-body-layout .loading-error--message');
    this.dom.$gridSmartViews = this.shadow.querySelector(
      `gz-push-body-layout ${WEB_COMPONENT_GENERIC_GRID_SMART_VIEWS}`
    ) as GenericGridSmartViewsWebComponent;
    this.dom.$smartViewsTitle = this.shadow.querySelector('gz-push-body-layout #smartViewsTitle');

    this.dom.$sideMenu = this.shadow.querySelector('gz-push-body-layout #sideMenu') as GzSideMenuWebComponent;
    this.dom.$riskManagementAdditionalText = this.shadow.querySelector('#riskManagementAdditionalText');
    this.dom.$scanEmptyStateButton = this.shadow.querySelector('#scan-button-empty-state');
    this.dom.$scanEmptyStateTooltip = this.shadow.querySelector('#scan-button-empty-state-tooltip');
    this.dom.$overlay = this.shadow.querySelector('#extra-details-context-menu');

    setBulkActions.call(this, {
      ignore: translateFn('Ignore applications'),
      restore: translateFn('Restore ignored applications'),
    });

    this.setTranslatedStrings();
  }

  setTranslatedStrings() {
    setTranslatedStrings.call(this);
    this.dom.$pageTitle.innerHTML = this.dependencies.translateFn('Vulnerabilities');
  }

  buildSuccessView(data: VulnerabilitiesAppWebComponentSchema) {
    this.buildSmartViews().then(async () => {
      if (data.externalFilters.length > 0) {
        await this.dom.$gridSmartViews.controller.setDefaultViewAsActiveView();
      }
      const activeView = this.dom.$gridSmartViews.controller.getActiveView();
      setSmartViewsPanelSizeBasedOnState(this.dom.$pushBodyLayout, this.dom.$gridSmartViews.smartViewState);
      await this.buildGrid(convertGenericViewConfigToGridViewConfig(activeView), data.externalFilters);
    });
  }

  buildErrorView() {
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.ERROR);
  }

  async buildGrid(view, externalFilters: Array<any> = []) {
    // Affected device type column should not be displayed
    view.displayedColumns = view.displayedColumns.filter(
      (column: ColumnIndexes) => column !== ColumnIndexes.AFFECTED_DEVICES_TYPE
    );

    const {
      hasExternalFilters,
      initialViewWithoutExternalFilters,
      view: processedView,
    } = processExternalFilters(view, externalFilters);

    this.dom.$grid.propertiesSet = false;

    setGridCommonProperties(this.dom.$grid, processedView, hasExternalFilters, initialViewWithoutExternalFilters);
    this.dom.$grid.dataProvider = new VulnerabilitiesAppGridDataProvider(this.dependencies);
    this.dom.$grid.allColumnDetails = this.getColumns();
    this.dom.$grid.filtersUiConfigs = this.getFiltersUiConfig();
    this.dom.$grid.columnRenderers = this.getColumnRenderers();
    this.dom.$grid.predefinedFiltersKeys = this.getPredefinedFiltersKeys();
    this.dom.$grid.sideMenuParent = this.dom.$wrapper;
    this.dom.$grid.filterDependencyMapping = this.filterDependencyMapping;
    this.dom.$grid.propertiesSet = true;

    this.dom.$sideMenu.closeSideMenu();

    if (!this.dom.$grid.started) {
      this.dom.$grid.injectDependencies(this.dependencies);
      await this.dom.$grid.start();
    }

    changeEmptyStateIcon(this.dom.$grid);
    this.removeElementClass(this.dom.$header, ElementClass.HIDDEN);
  }

  private getFiltersUiConfig() {
    return [
      getCompanyFilterUiConfig(ColumnKeys.COMPANY),
      {
        key: ColumnKeys.CVES,
        config: {
          filterLabel: this.dependencies.translateFn('CVE code'),
        },
      },
      {
        key: ColumnKeys.AFFECTED_DEVICES,
        config: {
          filterLabel: this.dependencies.translateFn('Resource name'),
        },
      },
    ];
  }

  private getColumnRenderers() {
    return {
      [ColumnKeys.COMPANY]: companyRenderer.bind(this),
      [ColumnKeys.OS]: osRenderer.bind(this),
      [ColumnKeys.CVES]: cvesRenderer.bind(this),
      [ColumnKeys.RISK_SCORE]: riskScoreRenderer.bind(this),
      [ColumnKeys.AFFECTED_DEVICES]: affectedDevicesRenderer.bind(this),
      [ColumnKeys.SELECT]: actionsColumnRenderer.bind(this),
      [ColumnKeys.NAME]: this.applicationNameRenderer.bind(this),
    };
  }

  private applicationNameRenderer(rowData) {
    return renderAsCellLabel(rowData?.item?.fullApplicationName);
  }

  private getPredefinedFiltersKeys() {
    const keys: any = [];

    if (!Application.Main.company.isCustomer()) {
      keys.push(ColumnKeys.COMPANY);
    }

    return [...keys, ColumnKeys.NAME, ColumnKeys.RISK_SCORE, ColumnKeys.CVES, ColumnKeys.AFFECTED_DEVICES];
  }

  private async buildSmartViews() {
    if (!(this.dom.$gridSmartViews instanceof GenericGridSmartViewsWebComponent)) {
      throw new Error('Missing grid smart views');
    }
    this.dom.$gridSmartViews.categoriesConfig = getSavedSmartViewsCategory(
      this.dependencies.componentName,
      this.getTranslations().defaultViewsTranslations,
      this.dependencies.api.clients.SmartViews.validateViewName
    );
    this.dom.$gridSmartViews.hasSearchInput = true;
    this.dom.$gridSmartViews.isEditedFn = this.isEdited.bind(this);
    this.dom.$gridSmartViews.injectDependencies(this.dependencies);

    await this.dom.$gridSmartViews.start();
    this.setElementClass(this.dom.$loader, ElementClass.HIDDEN);
    this.removeElementClass(this.dom.$gridSmartViews, ElementClass.HIDDEN);
  }

  isEdited() {
    return false;
  }

  getTranslations() {
    return {
      smartViewsStrings: this.stringsTranslator.getSmartViewsTranslations(),
      defaultViewsTranslations: {
        all_vulnerabilities: this.dependencies.translateFn('All vulnerabilities'),
        high_severity_vulnerabilities: this.dependencies.translateFn('High risk score'),
        ignored_vulnerabilities: this.dependencies.translateFn('Ignored'),
        watchlist_vulnerabilities: this.dependencies.translateFn('Watchlist'),
      },
    };
  }

  translateValidateViewNameErrorCode(errorCode: number) {
    if (errorCode === ValidateViewNameErrorCodeResponse.INVALID_VIEWNAME_LENGTH_CODE) {
      return this.dependencies.translateFn('A view name must have a maximum of 1000 characters.');
    }
    if (errorCode === ValidateViewNameErrorCodeResponse.INVALID_VIEWNAME_DUPLICATE_NAME_CODE) {
      return this.dependencies.translateFn('A view with this name already exists.');
    }
    if (errorCode === ValidateViewNameErrorCodeResponse.INVALID_VIEWNAME_INVALID_CHARACTERS) {
      return this.dependencies.translateFn('A view name must not contain special characters.');
    }
  }

  getColumns() {
    const allColumns: any = [];

    if (Application.Main.company.isPartner()) {
      allColumns.push({
        column: ColumnIndexes.COMPANY,
        key: ColumnKeys.COMPANY,
        columnName: translateFn('Company'),
        type: ColumnType.REMOTE_SELECT,
        removable: true,
        sortable: false,
        filterResetValue: {
          exactMatch: Application.Main.user.company.id,
        },
      });
    }

    allColumns.push(
      ...[
        {
          column: ColumnIndexes.NAME,
          key: ColumnKeys.NAME,
          columnName: this.dependencies.translateFn('Application name'),
          type: ColumnType.COMPLEX_ENUM_FILTER,
          width: '100px',
          removable: false,
          sortable: false,
          remoteDetails: {
            type: RemoteDetailsTypes.VulnerabilitiesName,
          },
        },
        {
          column: ColumnIndexes.RISK_SCORE,
          key: ColumnKeys.RISK_SCORE,
          columnName: this.dependencies.translateFn('Risk score'),
          type: ColumnType.RANGE,
          width: '100px',
          removable: false,
          sortable: true,
        },
        {
          column: ColumnIndexes.CVES,
          key: ColumnKeys.CVES,
          columnName: this.dependencies.translateFn('CVEs'),
          type: ColumnType.COMPLEX_ENUM_FILTER,
          removable: false,
          sortable: false,
          remoteDetails: {
            type: RemoteDetailsTypes.VulnerabilitiesCve,
          },
        },
        {
          column: ColumnIndexes.AFFECTED_DEVICES,
          key: ColumnKeys.AFFECTED_DEVICES,
          columnName: this.dependencies.translateFn('Affected resources'),
          type: ColumnType.COMPLEX_ENUM_FILTER,
          removable: false,
          sortable: false,
          remoteDetails: {
            type: RemoteDetailsTypes.VulnerabilitiesAffectedDevices,
          },
        },
        {
          column: ColumnIndexes.OS,
          key: ColumnKeys.OS,
          columnName: this.dependencies.translateFn('Platform'),
          type: ColumnType.ENUM,
          values: getOSTypeColumnValues(false),
          width: '100px',
          removable: true,
          sortable: false,
        },
        {
          column: ColumnIndexes.APPLICATION_TYPE,
          key: ColumnKeys.APPLICATION_TYPE,
          columnName: this.dependencies.translateFn('Application type'),
          type: ColumnType.ENUM,
          values: this.getApplicationTypeColumnValues(),
          width: '100px',
          removable: true,
          sortable: false,
        },
        {
          column: ColumnIndexes.STATE,
          key: ColumnKeys.STATE,
          columnName: this.dependencies.translateFn('State'),
          type: ColumnType.ENUM,
          values: getStateColumnValues(),
          width: '100px',
          removable: false,
          sortable: false,
        },
        {
          column: ColumnIndexes.EXPLOITS,
          key: ColumnKeys.EXPLOITS,
          columnName: this.dependencies.translateFn('Exploits'),
          type: ColumnType.ENUM,
          values: this.getAnswerColumnValues(),
          width: '100px',
          removable: true,
          sortable: false,
        },
        {
          column: ColumnIndexes.TARGETS_YOUR_INDUSTRY,
          key: ColumnKeys.TARGETS_YOUR_INDUSTRY,
          columnName: this.dependencies.translateFn('Targets your industry'),
          type: ColumnType.ENUM,
          values: this.getAnswerColumnValues(),
          width: '100px',
          removable: true,
          sortable: false,
        },
        {
          column: ColumnIndexes.AFFECTED_DEVICES_TYPE,
          key: ColumnKeys.AFFECTED_DEVICES_TYPE,
          columnName: this.dependencies.translateFn('Affected resource'),
          type: ColumnType.ENUM,
          values: this.getAffectedResourcesNoCloudResourcesColumnValues(),
          width: '100px',
          removable: false,
          sortable: false,
          isHiddenInGridSettings: true,
        },
        {
          column: ColumnIndexes.WATCHLIST,
          key: ColumnKeys.WATCHLIST,
          columnName: this.dependencies.translateFn('In watchlist'),
          type: ColumnType.ENUM,
          values: getWatchlistColumnValues(),
          width: '100px',
          removable: true,
          sortable: false,
        },
        {
          column: ColumnIndexes.SOURCE,
          key: ColumnKeys.SOURCE,
          columnName: this.dependencies.translateFn('Detection source'),
          type: ColumnType.ENUM,
          values: this.getSourceColumnValues(),
          width: '100px',
          removable: true,
          sortable: false,
        },
        {
          column: ColumnIndexes.SELECT,
          key: ColumnKeys.SELECT,
          columnName: '',
          type: ColumnType.ACTION,
          removable: false,
          sortable: false,
          width: '100px',
          filteringDisabled: true,
        },
      ]
    );

    return allColumns;
  }

  private getAffectedResourcesNoCloudResourcesColumnValues() {
    return [
      {
        label: translateFn('Not affected'),
        value: AffectedDeviceType.NOT_AFFECTED,
      },
      {
        label: translateFn('Endpoint'),
        value: AffectedDeviceType.ENDPOINT,
      },
      {
        label: translateFn('Server'),
        value: AffectedDeviceType.SERVER,
      },
      {
        label: translateFn('Container image'),
        value: AffectedDeviceType.CONTAINER_IMAGE,
      },
      {
        label: translateFn('Container'),
        value: AffectedDeviceType.CONTAINER,
      },
    ];
  }

  private getSourceColumnValues() {
    return [
      {
        label: BaseWebComponentTranslator.getInstance().getRiskManagementSource(VulnerabilitiesSources.ENDPOINT),
        value: VulnerabilitiesSources.ENDPOINT,
      },
      {
        label: BaseWebComponentTranslator.getInstance().getRiskManagementSource(VulnerabilitiesSources.NSVA),
        value: VulnerabilitiesSources.NSVA,
      },
    ];
  }

  private getApplicationTypeColumnValues() {
    const values = [ApplicationType.OS, ApplicationType.APPLICATION, ApplicationType.SERVICES];

    return values.map(value => {
      return {
        label: BaseWebComponentTranslator.getInstance().getRiskManagementApplicationType(value),
        value,
      };
    });
  }

  private getAnswerColumnValues() {
    const values = [AnswerType.YES, AnswerType.NO];

    return values.map(value => {
      return {
        label: BaseWebComponentTranslator.getInstance().getRiskManagementAnswerType(value),
        value,
      };
    });
  }
}
